import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Input, SkeletonList } from "@/design-system/components/ui";
import { StatusPill } from "../components/StatusPill";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { FilterToolbar } from "@/components/layout/FilterToolbar";
import { portalPackagesApi } from "../api/packages";
// Dates go through the PURE formatters with an EXPLICIT null zone, never the
// staff date-formatter hook in `@/utils/datetime` — that hook reads the staff
// AuthContext, which this tree never mounts. See portal/isolation.test.ts
// (FORBIDDEN_PREFIXES) and portal/portalInstantsRenderInOneZone.test.tsx.
import { formatDateInTz } from "@/design-system/lib/format";
import { PillToggle } from "../components/PillToggle";
import { useRefetchOnVisible } from "../components/useRefetchOnVisible";
import type { CollectionItemKind, PortalItemRowDto } from "../api/types";

// Post-Rollout PR 4 (PROB-39/40) — the Documents tab is the lifecycle
// "done" slice: items the staff has Approved or Waived, served directly by
// the backend (bucket=documents). Items move here from Outstanding on the
// staff Approved transition. Same DataTable + FilterToolbar primitives as
// the Outstanding tab; rows drill into the item surface to view/download.

const SOURCE_OPTIONS: Array<{ value: CollectionItemKind; label: string }> = [
  { value: "Upload", label: "Uploaded file" },
  { value: "ESign", label: "Signed" },
  { value: "Form", label: "Form" },
  { value: "Education", label: "Education" },
];

function sourceLabel(kind: CollectionItemKind): string {
  return SOURCE_OPTIONS.find((o) => o.value === kind)?.label ?? kind;
}

function itemHref(item: PortalItemRowDto): string {
  return `/portal/packages/${item.packageId}/items/${item.id}`;
}

export function DocumentsTab() {
  const [rows, setRows] = useState<PortalItemRowDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    try {
      const data = await portalPackagesApi.listMyItems("documents");
      setRows(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't load documents.");
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await portalPackagesApi.listMyItems("documents");
        if (!cancelled) setRows(data);
      } catch (err) {
        if (!cancelled)
          setError(err instanceof Error ? err.message : "Couldn't load documents.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useRefetchOnVisible(refetch);

  // Fetch a short-lived SAS for the item's filed document and open it in a
  // new tab — same pattern as the materials / education downloads. Scoping
  // is enforced server-side; a stale row that 403/404s surfaces the message.
  const openDocument = useCallback(async (itemId: string) => {
    try {
      const dl = await portalPackagesApi.getItemDocument(itemId);
      window.open(dl.sasUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't open that document.");
    }
  }, []);

  const [search, setSearch] = useState("");
  const [dealFilter, setDealFilter] = useState<Set<string>>(new Set());
  const [sourceFilter, setSourceFilter] = useState<Set<CollectionItemKind>>(new Set());
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const dealOptions = useMemo(() => {
    if (!rows) return [];
    const seen = new Map<string, string>();
    for (const r of rows) if (!seen.has(r.packageId)) seen.set(r.packageId, r.packageLabel);
    return [...seen.entries()].map(([id, label]) => ({ id, label }));
  }, [rows]);

  const filtered = useMemo<PortalItemRowDto[] | null>(() => {
    if (!rows) return null;
    const term = search.trim().toLowerCase();
    const fromMs = fromDate ? Date.parse(`${fromDate}T00:00:00Z`) : null;
    const toMs = toDate ? Date.parse(`${toDate}T23:59:59Z`) : null;
    return rows.filter((r) => {
      if (dealFilter.size > 0 && !dealFilter.has(r.packageId)) return false;
      if (sourceFilter.size > 0 && !sourceFilter.has(r.kind)) return false;
      if (term && !r.templateName.toLowerCase().includes(term)) return false;
      if (fromMs != null || toMs != null) {
        const at = r.decisionAt ? Date.parse(r.decisionAt) : null;
        if (at == null) return false;
        if (fromMs != null && at < fromMs) return false;
        if (toMs != null && at > toMs) return false;
      }
      return true;
    });
  }, [rows, search, dealFilter, sourceFilter, fromDate, toDate]);

  if (error) {
    return (
      <p className="text-small text-status-error" role="alert">
        {error}
      </p>
    );
  }
  if (!rows) {
    return (
      <SkeletonList data-testid="documents-loading" />
    );
  }

  const toggle = <T extends string>(
    setter: React.Dispatch<React.SetStateAction<Set<T>>>,
    value: T,
  ) =>
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });

  return (
    <section aria-label="Documents" className="flex flex-col gap-space-4">
      <FilterToolbar
        searchInput={
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by title…"
            data-testid="documents-search"
          />
        }
        filters={[
          ...(dealOptions.length > 1
            ? [
                {
                  key: "deal",
                  label: "Deal / Package",
                  control: (
                    <PillToggle
                      options={dealOptions.map((d) => ({ value: d.id, label: d.label }))}
                      selected={dealFilter}
                      onToggle={(v) => toggle(setDealFilter, v)}
                      testIdPrefix="documents-filter-deal"
                    />
                  ),
                },
              ]
            : []),
          {
            key: "source",
            label: "Source",
            control: (
              <PillToggle
                options={SOURCE_OPTIONS}
                selected={sourceFilter}
                onToggle={(v) => toggle(setSourceFilter, v)}
                testIdPrefix="documents-filter-source"
              />
            ),
          },
          {
            key: "date",
            label: "Approved",
            control: (
              <div className="flex items-center gap-space-1">
                <Input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  aria-label="Approved from"
                  data-testid="documents-filter-from"
                />
                <span className="text-caption text-gray-mid">–</span>
                <Input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  aria-label="Approved to"
                  data-testid="documents-filter-to"
                />
              </div>
            ),
          },
        ]}
      />

      <div data-testid="documents-list">
        <DataTable<PortalItemRowDto>
          rows={filtered ?? []}
          rowKey={(d) => d.id}
          sortable
          defaultSort={{ key: "approved", dir: "desc" }}
          pagination={{ pageSize: 25 }}
          groupBy={(d) => d.packageId}
          groupLabel={(_key, rowsInGroup) => rowsInGroup[0]?.packageLabel ?? "—"}
          emptyMessage={
            rows.length === 0
              ? "No documents yet. Once something you submit is approved, it'll show up here."
              : "No documents match those filters."
          }
          columns={documentsColumns(openDocument)}
        />
      </div>
    </section>
  );
}

function documentsColumns(
  onOpenDocument: (itemId: string) => void,
): Column<PortalItemRowDto>[] {
  return [
    {
      key: "title",
      header: "Title",
      sortable: true,
      sortAccessor: (d) => d.templateName,
      render: (d) => (
        <Link
          to={itemHref(d)}
          className="text-small font-medium text-onyx hover:text-gold-shadow"
          data-testid={`document-row-${d.id}`}
        >
          {d.templateName}
        </Link>
      ),
    },
    {
      key: "deal",
      header: "Deal / Package",
      mobileHidden: true,
      sortable: true,
      sortAccessor: (d) => d.packageLabel,
      render: (d) => (
        <span className="text-caption text-gray-mid">
          {d.packageLabel}
          {d.dealNumber ? ` · ${d.dealNumber}` : ""}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      sortAccessor: (d) => d.status,
      render: (d) => (
        <StatusPill status={d.status} data-testid={`document-status-${d.id}`} />
      ),
    },
    {
      key: "approved",
      header: "Approved",
      mobileHidden: true,
      sortable: true,
      sortAccessor: (d) => (d.decisionAt ? Date.parse(d.decisionAt) : 0),
      render: (d) => <ApprovedAtCell at={d.decisionAt} />,
    },
    {
      key: "document",
      header: "Document",
      render: (d) =>
        d.hasDocument ? (
          <button
            type="button"
            onClick={() => onOpenDocument(d.id)}
            // Negative margins offset the padding so the visual layout is
            // unchanged while the touch target grows to ~44px.
            className="text-small font-medium underline text-onyx hover:text-gold-shadow py-space-2 -my-space-2 px-space-2 -mx-space-2"
            data-testid={`document-open-${d.id}`}
          >
            View
          </button>
        ) : (
          <span className="text-caption text-gray-mid">—</span>
        ),
    },
    {
      key: "source",
      header: "Source",
      mobileHidden: true,
      render: (d) => (
        <span className="text-caption text-gray-mid" data-testid={`document-source-${d.id}`}>
          {sourceLabel(d.kind)}
        </span>
      ),
    },
  ];
}

function ApprovedAtCell({ at }: { at: string | null }) {
  return <span className="text-caption text-gray-mid">{at ? formatDateInTz(at, null) : "—"}</span>;
}
